/**
 * Image Optimization Service
 * Handles image processing including resizing, compression, and format conversion
 */

/**
 * Calculate new dimensions while maintaining aspect ratio
 * @param {number} originalWidth - Original image width
 * @param {number} originalHeight - Original image height
 * @param {number} maxWidth - Maximum width constraint
 * @param {boolean} maintainAspectRatio - Whether to maintain aspect ratio
 * @returns {Object} New dimensions {width, height}
 */
const calculateDimensions = (originalWidth, originalHeight, maxWidth, maintainAspectRatio = true) => {
  if (originalWidth <= maxWidth) {
    return { width: originalWidth, height: originalHeight };
  }

  if (maintainAspectRatio) {
    const ratio = originalHeight / originalWidth;
    return {
      width: maxWidth,
      height: Math.round(maxWidth * ratio)
    };
  }

  return { width: maxWidth, height: originalHeight };
};

/**
 * Load an image from a file
 * @param {File} file - Image file
 * @returns {Promise<HTMLImageElement>} Loaded image element
 */
const loadImage = (file) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (err) => reject(new Error('Error loading image: ' + err.message));
    img.src = URL.createObjectURL(file);
  });
};

/**
 * Optimize an image
 * @param {File} file - Original image file
 * @param {number} quality - Image quality (1-100)
 * @param {string} format - Output format (jpeg, png, webp)
 * @param {number} maxWidth - Maximum width
 * @param {boolean} maintainAspectRatio - Whether to maintain aspect ratio
 * @returns {Promise<Object>} Optimized image data with preview URL and size
 */
export const optimizeImage = async (
  file,
  quality = 80,
  format = 'jpeg',
  maxWidth = 1920,
  maintainAspectRatio = true
) => {
  try {
    // Load the image
    const img = await loadImage(file);
    
    // Calculate new dimensions
    const { width, height } = calculateDimensions(
      img.width,
      img.height,
      maxWidth,
      maintainAspectRatio
    );
    
    // Create a canvas element
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    
    // Draw the image on the canvas
    const ctx = canvas.getContext('2d');
    ctx.drawImage(img, 0, 0, width, height);
    
    // Convert to the specified format with the specified quality
    let mimeType;
    let qualityValue = quality / 100;
    
    switch (format) {
      case 'png':
        mimeType = 'image/png';
        break;
      case 'webp':
        mimeType = 'image/webp';
        break;
      case 'jpeg':
      default:
        mimeType = 'image/jpeg';
        break;
    }
    
    // Convert canvas to blob
    const blob = await new Promise((resolve) => {
      canvas.toBlob((blob) => resolve(blob), mimeType, qualityValue);
    });
    
    // Clean up the object URL
    URL.revokeObjectURL(img.src);
    
    // Create preview URL for the optimized image
    const preview = URL.createObjectURL(blob);
    
    return {
      preview,
      newSize: blob.size,
      width,
      height,
      format
    };
  } catch (error) {
    console.error('Image optimization error:', error);
    throw new Error(`Failed to optimize image: ${error.message}`);
  }
};

/**
 * Get supported image formats
 * @returns {Array} Array of supported formats
 */
export const getSupportedFormats = () => {
  const formats = ['jpeg', 'png'];
  
  // Check WebP support
  const canvas = document.createElement('canvas');
  if (canvas.toDataURL('image/webp').indexOf('data:image/webp') === 0) {
    formats.push('webp');
  }
  
  return formats;
};

